import React, { useCallback } from 'react'

import { RouteComponentProps, withRouter } from 'react-router-dom'

import { useOnPageChange } from 'common/use-on-page-change'
import { useTimer } from 'common/use-timer'

import { Context } from './context'
import { checkSEOMatch, setSEO } from './getters-setters'
import { ITitleAndDescriptionArgs } from './types'

interface IManageSEOContextProviderProps extends RouteComponentProps {
  children: React.ReactNode
}

type TRun = (argsconfig: ITitleAndDescriptionArgs) => void

/**
 * @describe WAIT_FOR_PAGE_TO_SET_SEO is a number that represents the time in milliseconds
 * The app will wait this time before setting the SEO to default values
 * assuming a page that loads will try to set it before that.
 */
const WAIT_FOR_PAGE_TO_SET_SEO = 500

/**
 * This component is a provider that centralizes the interactions between the app and the tags in the HEAD of the app that are related to SEO.
 * @param param0 props
 * @param param0.history history object from react-router-dom
 * @param param0.children children
 * @returns ManageSEOContextProvider React component
 */
export const ManageSEOContextProvider = ({ history, children }: IManageSEOContextProviderProps) => {
  const { clearTimer, setTimer } = useTimer()

  /**
   * @describe clearTimerAndRun
   * @param config is a object with title array and description string
   * @param run is a function that will be executed after clearTimer
   * @returns void
   */
  const clearTimerAndRun = useCallback(
    (config: ITitleAndDescriptionArgs, run: TRun) => {
      clearTimer()
      if (checkSEOMatch(config)) return

      run(config)
    },
    [clearTimer],
  )

  /**
   * @describe setSEOAfterTimer
   * @param args is a object with title array and description string
   * @returns void
   */
  const setSEOAfterTimer = useCallback(
    (args) => {
      setTimer(() => {
        setSEO(args)
      }, WAIT_FOR_PAGE_TO_SET_SEO)
    },
    [setTimer],
  )

  /**
   * @describe onSetSEO
   * @param args is a object with title array and description string
   * @returns void
   */
  const onSetSEO = useCallback(
    (args) => {
      clearTimerAndRun(args, setSEO)
    },
    [clearTimerAndRun],
  )

  /**
   * @describe onPageChange
   * @returns void
   */
  const onPageChange = useCallback(
    (_location, action) => {
      if (action === 'REPLACE') return
      clearTimerAndRun({}, setSEOAfterTimer)
    },
    [clearTimerAndRun, setSEOAfterTimer],
  )

  useOnPageChange(history, onPageChange)

  return <Context.Provider value={{ onSetSEO }}>{children}</Context.Provider>
}

const ManageSEOContextProviderWithRouter = withRouter(ManageSEOContextProvider)
export default ManageSEOContextProviderWithRouter
export { useSetSEO } from './use-set-seo'
export { default as SetSEO } from './SetSEO'
